import React, { useMemo } from 'react'
import { Ecc, qrCodeSvgPath } from '../../utils/qrcode'

interface QrCodeImageProps {
  /** The exact value to encode. For MFA this is the backend `otpauth://` URI. */
  value: string
  /** Rendered pixel size of the square. */
  size?: number
  /** Quiet-zone width in modules (QR spec minimum is 4). */
  border?: number
  /** Accessible description of the QR image. Must never contain the secret. */
  label: string
}

/**
 * Renders a QR code as a local, self-contained inline SVG. Nothing is uploaded
 * or fetched — the module matrix is computed in-browser from `value`. If the
 * value cannot be encoded, this throws so the caller can present the manual
 * fallback instead (see MfaEnrollment).
 */
export const QrCodeImage: React.FC<QrCodeImageProps> = ({ value, size = 220, border = 4, label }) => {
  const { path, dim } = useMemo(() => qrCodeSvgPath(value, Ecc.MEDIUM, border), [value, border])

  return (
    <svg
      role="img"
      aria-label={label}
      viewBox={`0 0 ${dim} ${dim}`}
      width={size}
      height={size}
      shapeRendering="crispEdges"
      style={{ display: 'block', width: size, height: 'auto', maxWidth: '100%', background: '#ffffff' }}
    >
      <rect width="100%" height="100%" fill="#ffffff" />
      <path d={path} fill="#000000" />
    </svg>
  )
}
